import { useEffect, useMemo, useState } from 'react';
import { Protect, useOrganization, useUser } from '@clerk/clerk-react';
import { format } from 'date-fns';
import { MoreHorizontal, UserPlus } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/toast';
import { SettingsHeader } from './components/SettingsHeader';

function initials(email) {
	if (!email) return '?';
	const local = email.split('@')[0];
	return (
		local
			.split(/[.\-_+]/)
			.filter(Boolean)
			.map((part) => part[0]?.toUpperCase())
			.slice(0, 2)
			.join('') || email[0]?.toUpperCase()
	);
}

function useTeamMembers() {
	const { user } = useUser();
	const { organization, isLoaded, invitations } = useOrganization({ invitations: { pageSize: 100 } });
	const [memberships, setMemberships] = useState(null);
	const [error, setError] = useState(null);
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		let cancelled = false;
		if (!organization) return undefined;
		(async () => {
			try {
				const { data } = await organization.getMemberships();
				if (!cancelled) setMemberships(data);
			} catch (err) {
				if (!cancelled) setError(err);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [organization, reloadKey]);

	const loading = !isLoaded || memberships === null;
	const revalidate = () => {
		setReloadKey((k) => k + 1);
		invitations?.revalidate?.();
	};

	const rows = useMemo(() => {
		const result = [];
		if (Array.isArray(memberships)) {
			memberships.forEach((m) => {
				result.push({
					id: `member-${m.id}`,
					kind: 'member',
					raw: m,
					emailAddress: m.publicUserData?.identifier,
					createdAt: m.createdAt,
					role: m.role,
					isMe: m.publicUserData?.userId === user?.id
				});
			});
		}
		if (invitations?.data) {
			invitations.data.forEach((inv) => {
				result.push({
					id: `invite-${inv.id}`,
					kind: 'invite',
					raw: inv,
					emailAddress: inv.emailAddress,
					createdAt: inv.createdAt,
					role: inv.role,
					isMe: false
				});
			});
		}
		return result;
	}, [memberships, invitations?.data, user?.id]);

	return { rows, loading, error, revalidate, organization };
}

function RoleLabel({ role }) {
	const label = role?.replace(/^org:/, '').replace(/_/g, ' ');
	return <span className="text-sm capitalize text-foreground">{label || '—'}</span>;
}

function MemberActions({ row, revalidate }) {
	const { toast } = useToast();
	if (row.isMe) return null;

	const remove = async () => {
		try {
			if (row.kind === 'invite') {
				await row.raw.revoke();
			} else {
				await row.raw.destroy();
			}
			revalidate();
		} catch (err) {
			toast({ title: 'Could not remove user', description: err?.message ?? 'Try again', variant: 'destructive' });
		}
	};

	return (
		<Protect permission="org:user_permissions:manage">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted"
						aria-label="Member actions"
					>
						<MoreHorizontal className="h-4 w-4" />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem onSelect={remove} className="text-destructive focus:text-destructive">
						{row.kind === 'invite' ? 'Revoke invite' : 'Remove from team'}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</Protect>
	);
}

function InviteDialog({ organization, revalidate }) {
	const [open, setOpen] = useState(false);
	const [email, setEmail] = useState('');
	const [role, setRole] = useState('org:admin');
	const [submitting, setSubmitting] = useState(false);
	const [roles, setRoles] = useState([]);
	const { toast } = useToast();

	useEffect(() => {
		if (!open || !organization) return;
		organization
			.getRoles()
			.then((res) => setRoles((res?.data || res || []).filter((r) => r.key !== 'org:member')))
			.catch(() => setRoles([]));
	}, [open, organization]);

	const submit = async (event) => {
		event.preventDefault();
		if (!email) return;
		setSubmitting(true);
		try {
			await organization.inviteMember({ emailAddress: email, role });
			setEmail('');
			setOpen(false);
			revalidate();
			toast({ title: 'Invite sent', description: `${email} will receive an email shortly.`, variant: 'success' });
		} catch (err) {
			toast({
				title: 'Could not send invite',
				description: err?.errors?.[0]?.longMessage ?? err?.message ?? 'Try again',
				variant: 'destructive'
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default" className="gap-1.5">
					<UserPlus className="h-4 w-4" /> Invite
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Invite a teammate</DialogTitle>
					<DialogDescription>Send an invite email. They&apos;ll get access as soon as they accept.</DialogDescription>
				</DialogHeader>
				<form onSubmit={submit} className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="invite-email">Email address</Label>
						<Input
							id="invite-email"
							type="email"
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							placeholder="teammate@yourshop.co.uk"
							required
						/>
					</div>
					{roles.length > 1 ? (
						<div className="space-y-1.5">
							<Label htmlFor="invite-role">Role</Label>
							<Select id="invite-role" value={role} onChange={(event) => setRole(event.target.value)}>
								{roles.map((r) => (
									<option key={r.key} value={r.key}>
										{r.name}
									</option>
								))}
							</Select>
						</div>
					) : null}
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="outline" type="button">
								Cancel
							</Button>
						</DialogClose>
						<Button type="submit" disabled={!email || submitting}>
							{submitting ? 'Sending…' : 'Send invite'}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

function TeamSkeleton() {
	return (
		<div className="max-w-4xl space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-10 w-72" />
			<Skeleton className="h-64" />
		</div>
	);
}

export function Team() {
	const { rows, loading, organization, revalidate } = useTeamMembers();

	if (loading) return <TeamSkeleton />;

	return (
		<div className="max-w-4xl space-y-8">
			<SettingsHeader
				eyebrow="Account"
				title="Team & access"
				description="People with access to this Unify account. Invite teammates to share day-to-day work."
				rightSlot={
					<Protect permission="org:user_permissions:manage">
						<InviteDialog organization={organization} revalidate={revalidate} />
					</Protect>
				}
			/>

			<SoftCard className="!p-0">
				<ul className="divide-y divide-border/60">
					{rows.length === 0 ? (
						<li className="p-6 text-sm text-muted-foreground">No teammates yet.</li>
					) : (
						rows.map((row) => (
							<li key={row.id} className="flex items-center gap-4 px-5 py-4 md:px-6">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground">
									{initials(row.emailAddress)}
								</div>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-2">
										<span className="truncate text-sm font-semibold text-foreground">{row.emailAddress}</span>
										{row.kind === 'invite' ? (
											<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
												Invited
											</span>
										) : null}
										{row.isMe ? (
											<span className="rounded-full bg-action-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-action-soft-foreground">
												You
											</span>
										) : null}
									</div>
									<div className="text-xs text-muted-foreground">
										{row.kind === 'invite'
											? 'Invite pending'
											: row.createdAt
												? `Joined ${format(new Date(row.createdAt), 'd MMM yyyy')}`
												: ''}
									</div>
								</div>
								<div className="hidden md:block">
									<RoleLabel role={row.role} />
								</div>
								<MemberActions row={row} revalidate={revalidate} />
							</li>
						))
					)}
				</ul>
			</SoftCard>
		</div>
	);
}
